/**
 * Типы состояния одной жизни.
 *
 * Здесь только данные и их формы; поведение — в birth.ts, tick.ts и далее.
 * `Decision` — форма решения из decisions/defs.ts (импорт только типа,
 * поэтому цикл types ↔ decisions безопасен).
 * `CauseId` — причины смерти ВОЗ из `@life/data` (causes.ts).
 */
import type { CauseId } from '@life/data';
import type { Decision } from './decisions/defs';
import type { Dream, DreamId, Goal } from './goals';
import type { Assets, Look } from './look';

export type { CauseId, Decision, Dream, DreamId, Goal };

export type Sex = 'f' | 'm';
export type Epoch = 'past' | 'present' | 'future' | 'any';
export type DrawMode = 'births' | 'population';
export type Education = 'none' | 'primary' | 'lowerSec' | 'upperSec' | 'vocational' | 'university';
export type Focus =
  | 'study'
  | 'play'
  | 'helpFamily'
  | 'sport'
  | 'hobby'
  | 'work'
  | 'partTime'
  | 'balance'
  | 'family'
  | 'rest'
  | 'faith'
  | 'career'
  | 'health'
  | 'care';

/** Видимые состояния, каждое в [0, 100]. */
export interface Stats {
  health: number;
  happiness: number;
  knowledge: number;
  relations: number;
}

export interface Job {
  titleKey: string;
  level: 0 | 1 | 2 | 3;
  /** Доход в долларах ППС за год. */
  income: number;
}

export interface LogEntry {
  age: number;
  key: string;
  params?: Record<string, string | number>;
  heavy?: boolean;
  starred?: boolean;
}

export interface Death {
  age: number;
  cause: CauseId;
  heavy: boolean;
}

export interface Life {
  id: string;
  seed: string;
  name: string;
  sex: Sex;
  iso3: string;
  birthYear: number;
  age: number;
  alive: boolean;
  stats: Stats;
  /** Доллары ППС, ≥ 0. */
  money: number;
  /** Долг, отдельное поле ≥ 0. */
  debt: number;
  job: Job | null;
  education: Education;
  inSchool: boolean;
  schoolYearsDone: number;
  urban: boolean;
  /** Дециль достатка семьи, 1..10. */
  familyDecile: number;
  siblings: number;
  partner: boolean;
  children: number;
  habits: { smoking: boolean; alcohol: boolean; gambling: boolean };
  conditions: string[];
  /** Флаги подсистем; строка — для ISO3 спонсора переезда (`sponsorIso3`). */
  flags: Record<string, number | boolean | string>;
  focus: Focus;
  log: LogEntry[];
  death: Death | null;
  pendingDecision: Decision | null;
  /** Внешность (H1): косметика, детерминирована от seed и региона. */
  look: Look;
  /** Жильё и машина (H1). */
  assets: Assets;
  /** Живых родителей, 0..2; дублирует `flags.parentsDead` в типизированном виде. */
  parentsAlive: number;
  /** Возраст каждого ребёнка; длина равна `children`. */
  childrenAges: number[];
  /** Очки действий на текущий год (energy.ts); пересчитываются тиком на смене года. */
  energy: number;
  /** Визиты по местам за текущий год: место → сколько раз; обнуляется на смене года. */
  visits: Record<string, number>;
  /** Мечта (goals.ts): выбрана в DREAM_AGE, `achievedAt` — возраст, когда сбылась. */
  dream: Dream | null;
  /** Предложенные мечты, пока игрок не выбрал (ответ — `YearInput.dream`). */
  pendingDream: DreamId[] | null;
  /** Цель года; остаётся, пока не выполнена. */
  yearGoal: Goal | null;
  /** Отмеченные вехи (идентификаторы MILESTONES), каждая один раз. */
  milestones: string[];
}

export interface BirthOptions {
  epoch: Epoch;
  drawMode: DrawMode;
  seed: string;
  name?: string;
  /** Фиксировать страну: тесты, приглашения. */
  iso3?: string;
}

/** Границы эпох по году рождения (включительно). */
export const EPOCH_YEARS: Readonly<Record<Exclude<Epoch, 'any'>, readonly [number, number]>> = {
  past: [1950, 1999],
  present: [2000, 2024],
  future: [2025, 2075],
};

/** Клампит значение состояния в [0, 100]; NaN → 0. */
export function clampStat(x: number): number {
  if (!(x >= 0)) return 0;
  return x > 100 ? 100 : x;
}
